<template>
  <InfuseTable
    :columns="columns"
    :rows="rows"
    :loading="loading"
    :search="search"
    :has-more-items="hasMoreItems"
    :disabled-rows="disabledRows"
    :removable="removable"
    :editable="editable"
    @selected="(value) => (selectedRows = value)"
    @search="(query: string) => emit('search', query)"
    @load="emit('load')"
    @click-row="(row) => emit('clickRow', row)"
    @remove="(row) => emit('remove', row)"
    @edit="(row) => emit('edit', row)"
    :remove-permission-ids="[organisationPermissions.DELETE, tenantOrganisationPermissions.DELETE]"
    :edit-permission-ids="[organisationPermissions.UPDATE, tenantOrganisationPermissions.UPDATE]"
    :no-data-title="t('iam.organisations.no_data_title')"
    :no-data-description="t('iam.organisations.no_data_description')"
  >
    <template #actions>
      <div class="create-organisation-button" />
      <InfuseButton
        data-testid="page-create-button"
        class="flex w-64 items-center"
        size="medium"
        @click="emit('create')"
      >
        <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
        {{ t('iam.organisations.header.add_button') }}
      </InfuseButton>
    </template>

    <template #noResultsActions>
      <div class="flex justify-center">
        <InfuseButton class="flex w-64 items-center" size="medium" @click="emit('create')">
          <font-awesome-icon :icon="['fas', 'plus']" class="mr-2" />
          {{ t('iam.organisations.header.add_button') }}
        </InfuseButton>
      </div>
    </template>

    <template #active="{ data }">
      <font-awesome-icon
        :icon="['fal', data ? 'circle-check' : 'circle-xmark']"
        class="h-5 w-5"
        :class="data ? 'text-success-500' : 'text-error-500'"
      />
    </template>
  </InfuseTable>
</template>

<script lang="ts" setup>
import { Organisation, organisationPermissions } from '@infuse/api/iam/organisation';
import { InfuseButton, InfuseTable } from '@infuse/ui/components';
import { type UiTableColumn, UiTableColumnAlignment } from '@infuse/ui/domain';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { tenantOrganisationPermissions } from '@infuse/api/iam/tenant';

const { t } = useI18n();

interface Emits {
  (e: 'load'): void;
  (e: 'clickRow', row: Organisation): void;
  (e: 'search', query: string): void;
  (e: 'remove', row: Organisation): void;
  (e: 'edit', row: Organisation): void;
  (e: 'create'): void;
}

const emit = defineEmits<Emits>();
const selectedRows = ref<Organisation[]>([]);

interface Props {
  rows: Organisation[];
  loading: boolean;
  page: number;
  searchQuery?: string;
  searchEnabled: boolean;
  removable?: boolean;
  editable?: boolean;
  hasMoreItems: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rows: () => [],
  loading: false,
  searchQuery: '',
  searchEnabled: true,
  removable: false,
  editable: false,
  hasMoreItems: true
});

const search = computed(() =>
  !props.searchEnabled
    ? undefined
    : {
        placeholder: t('iam.organisations.search_placeholder'),
        query: props.searchQuery
      }
);

const disabledRows = computed(() => {
  const row = selectedRows.value[0];
  if (!row) {
    return [];
  }
  return props.rows.filter((organisation) => organisation.id !== row.id);
});

const columns: UiTableColumn[] = [
  {
    name: 'name',
    label: () => t('iam.organisations.name'),
    field: 'name',
    bold: true
  },
  {
    name: 'description',
    label: () => t('iam.organisations.description'),
    field: 'description'
  },
  {
    name: 'tenant',
    label: () => t('iam.organisations.tenant'),
    field: 'tenantName'
  },
  {
    name: 'active',
    label: () => t('iam.organisations.active'),
    field: 'active',
    align: UiTableColumnAlignment.CENTER
  }
];
</script>
